import { useCallback, useMemo } from 'react';

import { useDispatch } from 'react-redux';

import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { WalkthroughStep } from '@bcp-nextgen-dx-component-factory/design-system';
import { rem } from '@helpers';
import { saveTempData, saveUserInput, useUserInput } from '@store';

interface WalkthroughResources {
	title: string;
	description: string;
	buttonText: string;
}

interface PaymentOptionsResources {
	titlePage: string;
	titleCard: string;
	acceptCheck: string;
	paymentTax: string;
	paymentTaxPrice: string;
	url: string;
	disclaimerOne: string;
	disclaimerTwo: string;
	withIva: string;
	valueWithoutPromotion: string;
	valueWithPromotion: string;
	walkthrough: WalkthroughResources;
	eur: string;
	month: string;
}

interface PaymentOptionsHelperOutput {
	resources: PaymentOptionsResources;
	acceptMasterOrVisaCards: boolean;
	solutionIntegrated: boolean;
	walkthroughSteps: WalkthroughStep[];
	handleCheckBoxOnClick: (checked: boolean) => void;
}

export const usePaymentOptionsHelper = (): PaymentOptionsHelperOutput => {
	const t = useTranslation();
	const dispatch = useDispatch();
	const { acceptMasterOrVisaCards, solutionIntegrated } = useUserInput();

	const resources = useMemo((): PaymentOptionsResources => {
		return {
			titlePage: t('placeholders.paymentOptions.title'),
			acceptCheck: t('placeholders.paymentOptions.accept.check'),
			disclaimerOne: t('placeholders.paymentOptions.disclaimer.one'),
			disclaimerTwo: t('placeholders.paymentOptions.disclaimer.two'),
			url: t('placeholders.paymentOptions.paymentTax.url'),
			paymentTax: t('general.paymentTax.text'),
			paymentTaxPrice: t('general.paymentTax.price.link'),
			titleCard: t('placeholders.paymentOptions.card.title'),
			withIva: t('general.withIva'),
			valueWithoutPromotion: t('general.valueWithoutPromotion'),
			valueWithPromotion: t('general.valueWithPromotion'),
			walkthrough: {
				title: t('walkthrough.title'),
				description: t('walkthrough.description'),
				buttonText: t('walkthrough.button'),
			},
			eur: ' ' + t('general.eur'),
			month: '/' + t('general.month'),
		};
	}, [t]);

	const walkthroughSteps: WalkthroughStep[] = [
		{
			title: resources.walkthrough.title,
			description: resources.walkthrough.description,
			button: {
				label: resources.walkthrough.buttonText.toUpperCase(),
				onPress: () => {
					dispatch(saveTempData({ hideCoachMarks: true }));
				},
			},
			highlightProps: {
				top: -48,
				width: rem(343),
				height: rem(69),
				borderRadius: rem(8),
			},
			coachMarkProps: {
				styles: { top: rem(-317), left: -18, width: rem(343) },
				arrowPlacement: 'bottom',
			},
		},
	];

	const handleCheckBoxOnClick = useCallback((checked: boolean) => {
		dispatch(saveUserInput({ acceptMasterOrVisaCards: checked }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		resources,
		acceptMasterOrVisaCards: acceptMasterOrVisaCards !== undefined ? acceptMasterOrVisaCards : false,
		solutionIntegrated: solutionIntegrated !== undefined ? solutionIntegrated : false,
		walkthroughSteps,
		handleCheckBoxOnClick,
	};
};
